"use client";

import type {ApprovalDecisionOutcome, ApprovalPosition} from "@/types/chatMessages";
import {snapshotValuesEqual, type MutationProposal} from "@/services/wingman/mutation-proposal";

import clsx from "clsx";
import {useId, useRef} from "react";
import {useTranslation} from "react-i18next";
import Button from "@/components/library/Button";
import Icon from "@/components/library/Icon";
import type {MessageContentFunctionCall, ToolApprovalResponse} from "@/services/wingman";
import MutationProposalReviewBar from "./MutationProposalReviewBar";
import UseCaseSnapshotDiff from "./UseCaseSnapshotDiff";
import ScriptSnapshotDiff, {changedFields} from "./ScriptSnapshotDiff";
import ToolApprovalNotices from "./ToolApprovalNotices";
import {useToolApprovalDecision} from "./useToolApprovalDecision";
import {proposalTitle, proposalTypeKey} from "./proposalIdentity";
import ApprovalDecisionStatus, {selectionClass, selectionLabelKey} from "./ApprovalDecisionStatus";

const EXPIRY_WARNING_MS = 2 * 60 * 1000;

const formatRemaining = (ms: number) => {
   const seconds = Math.ceil(ms / 1000);
   return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;
};

function expiryClass(expired: boolean, remainingMs: number): string {
   if (expired) return "bg-red-50 text-red-700 dark:bg-red-950/50 dark:text-red-300";
   if (remainingMs <= EXPIRY_WARNING_MS) return "bg-amber-50 text-amber-800 dark:bg-amber-950/50 dark:text-amber-200";
   return "text-500";
}

function ProposalExpiry({expired, remainingMs}: {expired: boolean; remainingMs?: number}) {
   const {t} = useTranslation("ask");
   if (remainingMs === undefined) return null;
   const time = formatRemaining(remainingMs);
   const expiresIn = t("toolApproval.expiresIn", {time});
   return <span
      aria-label={expired ? t("toolApproval.expiredShort") : expiresIn}
      title={expired ? t("toolApproval.expiredDetail") : expiresIn}
      className={clsx("mr-1 inline-flex items-center gap-1.5 rounded-full px-2 py-1 text-xs tabular-nums", expiryClass(expired, remainingMs))}>
      <Icon icon="clock" className="h-3 w-3" />
      {expired ? t("toolApproval.expiredShort") : expiresIn}
   </span>;
}

interface MutationProposalReviewPanelProps {
   navigation?: {index: number; total: number; previous?: () => void; next?: () => void};
   position?: ApprovalPosition;
   call: MessageContentFunctionCall;
   completed: boolean;
   result?: unknown;
   active: boolean;
   disabled: boolean;
   onRespond?: (decision: ToolApprovalResponse) => Promise<ApprovalDecisionOutcome>;
   onRequestFresh?: (proposal: MutationProposal) => Promise<void>;
   onClose: () => void;
}

/**
 * The review surface on the edited target's page: a floating decision bar, then exactly what
 * would change. A script editor below keeps showing the script as it is today; an Action
 * review replaces its editor until closed, so it opens what was saved once execution lands.
 */
export default function MutationProposalReviewPanel({call, completed, result, active, disabled, onRespond, onRequestFresh, onClose, position, navigation}: MutationProposalReviewPanelProps) {
   const {t} = useTranslation("ask");
   const targetId = useId();
   const changesRef = useRef<HTMLElement>(null);
   const decision = useToolApprovalDecision({call, completed, result, active, disabled, onRespond, onRequestFresh});
   const {status, settled, expired, error, ownerOnly, queued, canRequestFresh, requestFresh, requestedFresh, pending, saved} = decision;
   const proposal = call.approval?.proposal;
   if (!proposal) return null;

   const changeCount = changedFields(proposal).length;
   const actionable = active && !settled;

   return <>
      <MutationProposalReviewBar onClose={onClose} className={reviewBarClass(queued)}
         title={<><div className="mb-1 flex flex-wrap items-center gap-2 text-xs font-medium text-500">
            <span>{t(proposalTypeKey(proposal))}</span>
            {queued != null && <ApprovalDecisionStatus queued={queued} statusKey={`toolApproval.${status}`} />}
         </div><h2 id={targetId} className="text-sm font-semibold text-900">{proposalTitle(proposal)}</h2></>}
         subtitle={<p className="mt-1 flex min-w-0 flex-wrap items-center gap-1.5 text-xs text-500">
            {position && <><span className="font-semibold text-primary">{t("toolApproval.batch.position", position)}</span><span aria-hidden>·</span></>}
            {changeCount > 0 && <button type="button" onClick={() => changesRef.current?.scrollIntoView({behavior: "smooth", block: "start"})}
               className="shrink-0 font-medium text-700 underline-offset-2 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary">
               {t("toolApproval.changeCount", {count: changeCount})}
            </button>}
         </p>}
         actions={<ProposalDecisionActions decision={decision} actionable={actionable} targetId={targetId} position={position} />}>
         {actionable && position && <p className="mt-2 text-xs leading-relaxed text-600">{t(position.remaining === 1 && queued == null ? "toolApproval.batch.finalDecision" : "toolApproval.batch.singleDescription", {count: position.total})}</p>}
         <ToolApprovalNotices status={status} settled={settled} expired={expired} error={error} ownerOnly={ownerOnly} canRequestFresh={canRequestFresh} onRequestFresh={requestFresh} requestedFresh={requestedFresh} queued={queued} remaining={position?.remaining} className="mt-2 text-xs" />
         {navigation && <ProposalReviewNavigation navigation={navigation} pending={pending !== null} />}
      </MutationProposalReviewBar>

      <section ref={changesRef} aria-label={t("toolApproval.title")} className={clsx("@container mb-3 scroll-mt-28 overflow-hidden rounded-2xl border", selectionClass(queued) ?? "border-neutral bg-surface")}>
         {proposal.capability === "use_cases_update" && <div className="flex flex-wrap items-center justify-between gap-2 border-b border-neutral px-4 py-3 text-xs text-500">
            <span>{t(status === "completed" ? "toolApproval.savedChanges" : "toolApproval.suggestedChanges")}</span>
            <span className="flex items-center gap-3">
               <span className="flex items-center gap-1.5"><span aria-hidden className="h-2 w-2 rounded-sm bg-red-200 dark:bg-red-800" />{t("toolApproval.removed")}</span>
               <span className="flex items-center gap-1.5"><span aria-hidden className="h-2 w-2 rounded-sm bg-green-200 dark:bg-green-800" />{t("toolApproval.added")}</span>
            </span>
         </div>}
         {proposal.capability === "use_cases_update" ? <UseCaseSnapshotDiff preview={proposal} saved={saved}
            savedOpen={!!saved && !snapshotValuesEqual(saved, proposal.after)} /> : <ScriptSnapshotDiff preview={proposal} />}
      </section>

      {proposal.capability !== "use_cases_update" && <div className="mb-3 flex items-center gap-3 pt-1 text-xs text-500" role="separator">
         <span className="h-px flex-1 bg-neutral" />
         <span className="shrink-0">{t(actionable ? "toolApproval.currentScript" : "toolApproval.currentScriptSettled")}</span>
         <span className="h-px flex-1 bg-neutral" />
      </div>}
   </>;
}

function reviewBarClass(queued: boolean | null) {
   if (queued === true) return "border-neutral bg-panel";
   if (queued === false) return "border-amber-400/60 bg-amber-50 dark:bg-amber-950";
   return undefined;
}

function proposalDecisionLabel(approved: boolean, decision: ReturnType<typeof useToolApprovalDecision>, position?: ApprovalPosition) {
   if (decision.pending === (approved ? "apply" : "discard")) return approved ? "toolApproval.applying" : "toolApproval.discarding";
   if (decision.queued === approved) return selectionLabelKey(approved);
   if (position) return approved ? "toolApproval.batch.approveThis" : "toolApproval.batch.rejectThis";
   return approved ? "toolApproval.applyChanges" : "toolApproval.discard";
}

function ProposalDecisionActions({decision, actionable, targetId, position}: {
   decision: ReturnType<typeof useToolApprovalDecision>;
   actionable: boolean;
   targetId: string;
   position?: ApprovalPosition;
}) {
   const {t} = useTranslation("ask");
   const {status, expired, remainingMs, pending, queued, canRespond, canApply, respond} = decision;
   let applyIcon;
   if (pending === "apply") applyIcon = <Icon icon="spinner" className="h-3 w-3 animate-spin" />;
   else if (queued === true) applyIcon = <Icon icon="check" className="h-3 w-3" />;

   return <>
      {actionable && <ProposalExpiry expired={expired} remainingMs={remainingMs} />}
      {actionable ? <fieldset aria-labelledby={targetId} className="flex min-w-0 flex-wrap justify-end gap-2">
         <Button type="tertiary" compact disabled={!canRespond} aria-pressed={queued === false} onClick={() => void respond(false)}
            iconBefore={queued === false ? <Icon icon="xmark" className="h-3 w-3" /> : undefined}>
            {t(proposalDecisionLabel(false, decision, position))}
         </Button>
         <Button type={queued === true ? "tertiary" : "primary"} compact disabled={!canApply} aria-pressed={queued === true} onClick={() => void respond(true)}
            className="aria-pressed:bg-100 aria-pressed:text-600" iconBefore={applyIcon}>
            {t(proposalDecisionLabel(true, decision, position))}
         </Button>
      </fieldset> : <output className={clsx("inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium", status === "completed"
         ? "bg-green-50 text-green-700 dark:bg-green-950/50 dark:text-green-300" : "bg-100 text-600")}>
         {status === "completed" && <Icon icon="check" className="h-3 w-3" />}{t(`toolApproval.${status}`)}
      </output>}
   </>;
}

function ProposalReviewNavigation({navigation, pending}: {
   navigation: NonNullable<MutationProposalReviewPanelProps["navigation"]>;
   pending: boolean;
}) {
   const {t} = useTranslation("ask");
   return <nav aria-label={t("toolApproval.reviewNavigation")} className="mt-3 flex items-center justify-between gap-2 border-t border-neutral pt-3">
      <span className="text-xs tabular-nums text-500">{t("toolApproval.reviewPosition", {index: navigation.index, total: navigation.total})}</span>
      <div className="flex items-center gap-2">
         <Button type="tertiary" compact disabled={!navigation.previous || pending} onClick={() => navigation.previous?.()}
            iconBefore={<Icon icon="arrow-left" className="h-3 w-3" />}>{t("toolApproval.previousChange")}</Button>
         <Button type="secondary" compact disabled={!navigation.next || pending} onClick={() => navigation.next?.()}
            iconAfter={<Icon icon="arrow-right" className="h-3 w-3" />}>{t("toolApproval.nextChange")}</Button>
      </div>
   </nav>;
}
